import axios from "axios";
import { useCallback, useEffect, useRef, useState } from "react";

export const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
export const api = axios.create({ baseURL: API_BASE, withCredentials: true });

let branchId = localStorage.getItem("pf_branch") || "";
export const setBranchId = (id) => { branchId = id || ""; localStorage.setItem("pf_branch", branchId); };

api.interceptors.request.use((cfg) => {
  if (branchId) cfg.headers["X-Branch-Id"] = branchId;
  return cfg;
});

api.interceptors.response.use((r) => r, async (err) => {
  const orig = err.config || {};
  if (err.response?.status === 401 && !orig._retry && !String(orig.url).includes("/auth/")) {
    orig._retry = true;
    try { await api.post("/auth/refresh"); return api(orig); } catch (e) { window.dispatchEvent(new Event("pf:logout")); }
  }
  return Promise.reject(err);
});

export function errMsg(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message === "Network Error" ? "Network error — check your connection and retry." : "Something went wrong. Please try again.";
  if (typeof d === "string") return d;
  if (d?.message) return d.message;
  if (Array.isArray(d)) return d.map((x) => (x?.msg ? `${(x.loc || []).slice(-1)[0]}: ${x.msg}` : JSON.stringify(x))).join(" · ");
  return d.msg || String(d);
}

export function useApi(url, deps = []) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const seq = useRef(0);
  const load = useCallback(async () => {
    if (!url) return;
    const n = ++seq.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    try { const r = await api.get(url); if (n === seq.current) setState({ data: r.data, loading: false, error: null }); }
    catch (e) { if (n === seq.current) setState({ data: null, loading: false, error: errMsg(e) }); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, ...deps]);
  useEffect(() => { load(); }, [load]);
  return { ...state, reload: load };
}

export function useAllProducts() {
  const [items, setItems] = useState([]);
  useEffect(() => {
    let live = true;
    (async () => {
      const out = [];
      for (let page = 1; page < 100; page++) {
        const r = (await api.get(`/products?size=200&page=${page}`)).data;
        out.push(...(r.items || []));
        if ((r.items || []).length < 200) break;
      }
      if (live) setItems(out);
    })().catch(() => {});
    return () => { live = false; };
  }, []);
  return { data: { items } };
}

export const ghs = (v) => `GHS ${((v || 0) / 100).toLocaleString("en-GH", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
export const money = (v) => (v == null ? "Unavailable" : ghs(v));
export const toMinor = (s) => { const n = parseFloat(String(s).replace(/,/g, "")); return Number.isFinite(n) ? Math.round(n * 100) : 0; };
export const fromMinor = (v) => ((v || 0) / 100).toFixed(2);
const _p = (n) => String(n).padStart(2, "0");
const _parts = (iso, opts) => Object.fromEntries(new Intl.DateTimeFormat("en-GB", { timeZone: "Africa/Accra", hourCycle: "h23", ...opts }).formatToParts(new Date(iso)).map((p) => [p.type, p.value]));
export const fmtDate = (iso) => {
  if (!iso) return "—";
  const s = String(iso);
  if (/^\d{4}-\d{2}-\d{2}$/.test(s)) return `${s.slice(8, 10)}/${s.slice(5, 7)}/${s.slice(0, 4)}`;
  if (/^\d{4}-\d{2}$/.test(s)) return `${s.slice(5, 7)}/${s.slice(0, 4)}`;
  if (isNaN(new Date(s))) return s;
  const p = _parts(s, { day: "2-digit", month: "2-digit", year: "numeric" });
  return `${p.day}/${p.month}/${p.year}`;
};
export const fmtDateTime = (iso) => {
  if (!iso) return "—";
  const p = _parts(iso, { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
  return `${p.day}/${p.month}/${p.year} ${_p(p.hour)}:${p.minute}`;
};
export const fmtTime = (iso) => { const p = _parts(iso || Date.now(), { hour: "2-digit", minute: "2-digit" }); return `${_p(p.hour)}:${p.minute}`; };
export const todayAccra = () => new Date().toLocaleDateString("en-CA", { timeZone: "Africa/Accra" });
export const daysAgo = (n) => { const d = new Date(); d.setDate(d.getDate() - n); return d.toLocaleDateString("en-CA", { timeZone: "Africa/Accra" }); };
export const uuid = () => (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
export const ROLE_LABEL = { platform_admin: "Platform admin", owner: "Owner", branch_manager: "Branch manager", pharmacist: "Pharmacist", cashier: "Cashier", inventory_officer: "Inventory officer" };

export async function openPdf(url) {
  const r = await api.get(url, { responseType: "blob" });
  window.open(URL.createObjectURL(r.data), "_blank");
}

export async function downloadCsv(url, filename) {
  const r = await api.get(url, { responseType: "blob" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(r.data);
  a.download = filename;
  a.click();
}
